import React from 'react';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import SentimentDissatisfiedRoundedIcon from '@mui/icons-material/SentimentDissatisfiedRounded';
import SentimentNeutralRoundedIcon from '@mui/icons-material/SentimentNeutralRounded';
import SentimentSatisfiedAltRoundedIcon from '@mui/icons-material/SentimentSatisfiedAltRounded';

import { CS_FONT, CS_WEIGHT, csTokens } from 'components/CallScribe/callScribeTheme';

export type TChipTone = 'neutral' | 'positive' | 'negative' | 'warning' | 'info' | 'outline';

const useChipStyles = makeStyles((theme: Theme) => {
  const t = csTokens(theme);
  return {
    chip: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      height: 22,
      maxWidth: '100%',
      boxSizing: 'border-box',
      padding: '0 8px 0 6px',
      borderRadius: 7,
      ...CS_FONT.caption,
      lineHeight: '22px',
      fontWeight: CS_WEIGHT.strong,
      whiteSpace: 'nowrap',
      background: t.neuBg,
      color: t.neu,
      '& svg': { fontSize: 14, flex: '0 0 auto' },
    },
    label: {
      ...CS_FONT.caption,
      lineHeight: '22px',
      fontWeight: 'inherit',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      minWidth: 0,
    },
    positive: { background: t.posBg, color: t.pos },
    negative: { background: t.negBg, color: t.neg },
    neutral: { background: t.neuBg, color: t.neu },
    warning: { background: t.warnBg, color: t.warn },
    info: { background: t.infoBg, color: t.info },
    outline: {
      background: 'transparent',
      boxShadow: `inset 0 0 0 1px ${t.lineStrong}`,
      color: t.text2,
      fontWeight: 600,
      padding: '0 8px',
    },
  };
});

export interface IChipProps {
  tone?: TChipTone;
  icon?: React.ReactElement;
  title?: string;
  children: React.ReactNode;
}

const Chip: React.FC<IChipProps> = ({ tone = 'neutral', icon, title, children }) => {
  const classes = useChipStyles();
  return (
    <span data-chip data-tone={tone} title={title} className={`${classes.chip} ${classes[tone]}`}>
      {icon ? React.cloneElement(icon, { 'aria-hidden': true, focusable: 'false' }) : null}
      <span className={classes.label}>{children}</span>
    </span>
  );
};

export interface IStatusChipProps {
  label: string;
  tone?: TChipTone;
  icon?: React.ReactElement;
}

export const StatusChip: React.FC<IStatusChipProps> = ({ label, tone = 'outline', icon }) => (
  <Chip tone={tone} icon={icon ?? <CheckRoundedIcon />}>
    {label}
  </Chip>
);

export type TSentiment = 'positive' | 'negative' | 'neutral';

const SENTIMENT: Record<TSentiment, { word: string; icon: React.ReactElement }> = {
  positive: { word: 'Positive', icon: <SentimentSatisfiedAltRoundedIcon /> },
  negative: { word: 'Negative', icon: <SentimentDissatisfiedRoundedIcon /> },
  neutral: { word: 'Neutral', icon: <SentimentNeutralRoundedIcon /> },
};

export const SentimentChip: React.FC<{ sentiment: TSentiment }> = ({ sentiment }) => (
  <Chip tone={sentiment} icon={SENTIMENT[sentiment].icon}>
    {SENTIMENT[sentiment].word}
  </Chip>
);

export interface ITimeEntryChipProps {
  state: 'posted' | 'suggested';
  minutes?: number | null;
}

export const TimeEntryChip: React.FC<ITimeEntryChipProps> = ({ state, minutes }) => {
  if (state === 'posted') {
    return (
      <Chip tone="info" icon={<ScheduleRoundedIcon />}>
        Time posted
      </Chip>
    );
  }
  const amount = typeof minutes === 'number' && minutes > 0 ? `${Math.round(minutes)}m` : 'Time';
  return (
    <Chip tone="warning" icon={<ScheduleRoundedIcon />}>
      {`${amount} suggested`}
    </Chip>
  );
};

export const TopicChip: React.FC<{ label: string }> = ({ label }) => (
  <Chip tone="outline" title={label}>
    {label}
  </Chip>
);

export default Chip;
